/**
 * Pure validation and mapping logic for the Add Connector form.
 *
 * Extracted from AddConnectorPopup to enable property-based testing
 * without React component overhead.
 */

export interface ConnectorType {
  type: string
  displayName: string
}

export interface DropdownItem {
  value: string
  label: string
}

/**
 * A single credential field descriptor, as declared by a connector config's
 * `credentialFields`. Structurally compatible with `CredentialField` from
 * `@hydrologiq/shared` — kept local so this module stays pure and dependency-free.
 */
export interface CredentialFieldSpec {
  /** JSON key written into the vaultSecret blob. */
  key: string
  /** Whether the field must be non-empty to submit. Defaults to true when undefined. */
  required?: boolean
}

/**
 * Determines whether the Add Connector form can be submitted.
 *
 * `connectorType` and `connectorName` must always contain at least one
 * non-whitespace character.
 *
 * Credential requirement depends on the selected connector type:
 * - When `credentialFields` is provided and non-empty, every REQUIRED field
 *   (a field is required unless `required === false`) must have a non-empty,
 *   non-whitespace value in `credentialValues`. The `apiToken` is ignored on
 *   this path.
 * - Otherwise (no `credentialFields`), the existing behavior applies: `apiToken`
 *   must be non-empty and non-whitespace.
 *
 * This function is pure: no React, no side effects.
 *
 * @param connectorType - The selected connector type value
 * @param connectorName - The entered connector name
 * @param apiToken - The entered API token (single-field / opaque-credential path)
 * @param credentialFields - Optional multi-field credential descriptors for the
 *   selected connector type. When present and non-empty, drives validation.
 * @param credentialValues - Optional map of collected credential values keyed by
 *   field key. Used only when `credentialFields` is present.
 * @returns true if the form is valid and can be submitted
 */
export function canSubmitForm(
  connectorType: string,
  connectorName: string,
  apiToken: string,
  credentialFields?: CredentialFieldSpec[],
  credentialValues?: Record<string, string>
): boolean {
  if (connectorType.trim().length === 0 || connectorName.trim().length === 0) {
    return false
  }

  if (credentialFields && credentialFields.length > 0) {
    const values = credentialValues ?? {}
    return credentialFields.every(
      (field) =>
        field.required === false ||
        (values[field.key] ?? "").trim().length > 0
    )
  }

  return apiToken.trim().length > 0
}

/**
 * Builds the single opaque `vaultSecret` string submitted to
 * `createIntegrationSource`.
 *
 * Serialization semantics (single source of truth for the Add Connector form):
 * - When `credentialFields` is provided and non-empty, iterate the fields in
 *   declared order and build a JSON object whose keys are exactly the declared
 *   field keys and whose values are the entered values trimmed. A missing key in
 *   `credentialValues` serializes as an empty string (`""`). The result is
 *   `JSON.stringify(blob)`.
 * - Otherwise (no `credentialFields`), fall back to the single opaque credential:
 *   the trimmed `apiToken`.
 *
 * This function is pure: no React, no side effects.
 *
 * @param credentialFields - Optional multi-field credential descriptors for the
 *   selected connector type. When present and non-empty, drives serialization.
 * @param credentialValues - Map of collected credential values keyed by field
 *   key. Used only when `credentialFields` is present and non-empty.
 * @param apiToken - The entered API token (single-field / opaque-credential path)
 * @returns The serialized `vaultSecret` string
 */
export function buildVaultSecret(
  credentialFields: CredentialFieldSpec[] | undefined,
  credentialValues: Record<string, string>,
  apiToken: string
): string {
  if (credentialFields && credentialFields.length > 0) {
    const blob: Record<string, string> = {}
    for (const field of credentialFields) {
      blob[field.key] = (credentialValues[field.key] ?? "").trim()
    }
    return JSON.stringify(blob)
  }
  return apiToken.trim()
}

/**
 * Maps an array of connector types to dropdown items for the type selector.
 *
 * Each connector type's `displayName` becomes the dropdown label,
 * and its `type` becomes the dropdown value.
 * A placeholder item is prepended (with empty value).
 *
 * @param connectorTypes - Array of connector types from the backend
 * @param loading - Whether connector types are still loading
 * @returns Array of dropdown items with placeholder + mapped types
 */
export function mapConnectorTypesToDropdownItems(
  connectorTypes: ConnectorType[],
  loading: boolean
): DropdownItem[] {
  return [
    { value: "", label: loading ? "Loading..." : "Select connector type..." },
    ...connectorTypes.map((ct) => ({
      value: ct.type,
      label: ct.displayName,
    })),
  ]
}

/**
 * Returns the auto-filled connector name for a given selected type.
 *
 * When a user selects a connector type from the dropdown, the name field
 * is populated with the display name of that type.
 *
 * @param connectorTypes - Array of available connector types
 * @param selectedType - The selected connector type value
 * @returns The display name of the matching type, or undefined if not found
 */
export function getAutoFilledName(
  connectorTypes: ConnectorType[],
  selectedType: string
): string | undefined {
  const selected = connectorTypes.find((ct) => ct.type === selectedType)
  return selected?.displayName
}
